import { useEffect, useState } from "react";
import {
  Download,
  MessageSquareWarning,
  Plus,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Loading, StatCard, SocietyPageHeader } from "../../components/shared";

const empty = {
  ticket_type: "COMPLAINT",
  priority: "NORMAL",
  location_type: "UNIT",
  subject: "",
};

export default function Helpdesk() {
  const [boot, setBoot] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "" });
  const [create, setCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, v]) => v),
      );
      const [b, t, d] = await Promise.all([
        api.get("/society-helpdesk/bootstrap"),
        api.get(`/society-helpdesk/tickets?${query}`),
        api.get("/society-helpdesk/dashboard"),
      ]);
      setBoot(b.data);
      setTickets(t.data);
      setDashboard(d.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Helpdesk");
    }
  };
  useEffect(() => {
    load();
  }, [filters.q, filters.status]);
  if (!boot || !tickets || !dashboard) return <Loading />;
  return (
    <div data-testid="helpdesk-page">
      <SocietyPageHeader
        icon={MessageSquareWarning}
        tone="green"
        title="Complaints & Helpdesk"
        subtitle="One operational ticket engine for complaints and service requests."
      >
        <Button data-testid="helpdesk-refresh" onClick={load} variant="outline">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
        <Button data-testid="helpdesk-create" onClick={() => setCreate(true)}>
          <Plus className="h-4 w-4" />
          New ticket
        </Button>
      </SocietyPageHeader>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={MessageSquareWarning}
          label="Open tickets"
          testid="helpdesk-open-kpi"
          value={String(dashboard.open)}
        />
        <StatCard
          icon={ShieldAlert}
          label="Urgent"
          testid="helpdesk-urgent-kpi"
          tone="rose"
          value={String(dashboard.urgent)}
        />
        <StatCard
          icon={MessageSquareWarning}
          label="SLA breached"
          testid="helpdesk-sla-kpi"
          tone="amber"
          value={String(dashboard.sla_breached)}
        />
        <StatCard
          icon={MessageSquareWarning}
          label="In progress"
          testid="helpdesk-progress-kpi"
          tone="secondary"
          value={String(dashboard.in_progress)}
        />
      </section>
      <div className="mt-7 flex flex-wrap gap-3">
        <Input
          className="max-w-sm"
          data-testid="helpdesk-search"
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          placeholder="Search ticket, subject, requester or unit"
          value={filters.q}
        />
        <select
          className="border bg-background px-3 py-2 text-sm"
          data-testid="helpdesk-status-filter"
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          value={filters.status}
        >
          <option value="">All statuses</option>
          {[
            "NEW",
            "ACKNOWLEDGED",
            "ASSIGNED",
            "IN_PROGRESS",
            "ON_HOLD",
            "WAITING_FOR_RESIDENT",
            "WAITING_FOR_VENDOR",
            "RESOLVED",
            "CLOSED",
            "REOPENED",
            "CANCELLED",
          ].map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
        <a
          data-testid="helpdesk-export-csv"
          href={`${API}/society-helpdesk/reports/register.csv`}
        >
          <Button variant="outline">
            <Download className="h-4 w-4" />
            CSV
          </Button>
        </a>
        <a
          data-testid="helpdesk-export-xlsx"
          href={`${API}/society-helpdesk/reports/register.xlsx`}
        >
          <Button variant="outline">XLSX</Button>
        </a>
      </div>
      <CategoryPanel categories={boot.categories} onChanged={load} />
      <div
        className="mt-5 overflow-x-auto border bg-card"
        data-testid="helpdesk-ticket-list"
      >
        <table className="min-w-[960px] w-full text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="p-3 text-left">Ticket</th>
              <th className="p-3 text-left">Subject</th>
              <th className="p-3 text-left">Unit / location</th>
              <th className="p-3 text-left">Priority</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">SLA</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? (
              <tr>
                <td
                  className="p-10 text-center text-muted-foreground"
                  colSpan={7}
                >
                  No tickets match these filters.
                </td>
              </tr>
            ) : (
              tickets.map((ticket) => (
                <tr
                  className="border-t"
                  data-testid={`helpdesk-ticket-${ticket.ticket_id}`}
                  key={ticket.ticket_id}
                >
                  <td className="p-3 font-mono text-xs">
                    {ticket.ticket_number}
                    <br />
                    <span className="font-sans text-muted-foreground">
                      {ticket.ticket_type}
                    </span>
                  </td>
                  <td className="p-3">
                    <b>{ticket.subject}</b>
                    <br />
                    <span className="text-muted-foreground">
                      {ticket.category_name} ·{" "}
                      {ticket.requester_name || "Office"}
                    </span>
                  </td>
                  <td className="p-3">
                    {ticket.unit_number || ticket.location || "Common area"}
                  </td>
                  <td className="p-3">
                    {ticket.emergency ? "Emergency · " : ""}
                    {ticket.priority}
                  </td>
                  <td className="p-3">{ticket.status}</td>
                  <td className="p-3">{ticket.sla.status}</td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`helpdesk-open-${ticket.ticket_id}`}
                      onClick={() => setSelected(ticket.ticket_id)}
                      size="sm"
                      variant="outline"
                    >
                      Open
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TicketCreate
        boot={boot}
        onClose={() => setCreate(false)}
        onSaved={() => {
          setCreate(false);
          load();
        }}
        open={create}
      />
      <TicketDetail
        boot={boot}
        id={selected}
        onClose={() => setSelected(null)}
        onChanged={load}
      />
    </div>
  );
}

function TicketCreate({ boot, onClose, onSaved, open }) {
  const [form, setForm] = useState(empty);
  const save = async () => {
    try {
      await api.post("/society-helpdesk/tickets", {
        ...form,
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Ticket created");
      setForm(empty);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create ticket");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        data-testid="helpdesk-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            Create helpdesk ticket
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Input
            data-testid="helpdesk-subject"
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Subject"
            value={form.subject}
          />
          <select
            data-testid="helpdesk-type"
            onChange={(e) => setForm({ ...form, ticket_type: e.target.value })}
            value={form.ticket_type}
          >
            {boot.ticket_types.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
          <select
            data-testid="helpdesk-category"
            onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            value={form.category_id || ""}
          >
            <option value="">Select category</option>
            {boot.categories
              .filter((x) => x.active)
              .map((x) => (
                <option key={x.category_id} value={x.category_id}>
                  {x.name}
                </option>
              ))}
          </select>
          <select
            data-testid="helpdesk-priority"
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            value={form.priority}
          >
            {["LOW", "NORMAL", "HIGH", "URGENT"].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
          <select
            data-testid="helpdesk-asset"
            onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
            value={form.asset_id || ""}
          >
            <option value="">Linked asset (optional)</option>
            {(boot.assets || []).map((asset) => (
              <option key={asset.asset_id} value={asset.asset_id}>
                {asset.asset_code} · {asset.name}
              </option>
            ))}
          </select>
          <select
            data-testid="helpdesk-location-type"
            onChange={(e) =>
              setForm({ ...form, location_type: e.target.value, unit_id: "" })
            }
            value={form.location_type}
          >
            <option value="UNIT">Unit</option>
            <option value="COMMON_AREA">Common area</option>
          </select>
          {form.location_type === "UNIT" ? (
            <select
              data-testid="helpdesk-unit"
              onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
              value={form.unit_id || ""}
            >
              <option value="">Select unit</option>
              {boot.units.map((x) => (
                <option key={x.unit_id} value={x.unit_id}>
                  {x.unit_number}
                </option>
              ))}
            </select>
          ) : (
            <Input
              data-testid="helpdesk-common-location"
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Common area location"
              value={form.location || ""}
            />
          )}
          <select
            data-testid="helpdesk-requester"
            onChange={(e) =>
              setForm({ ...form, requester_person_id: e.target.value })
            }
            value={form.requester_person_id || ""}
          >
            <option value="">Requester (optional)</option>
            {boot.people.map((x) => (
              <option key={x.person_id} value={x.person_id}>
                {x.display_name}
              </option>
            ))}
          </select>
          <Textarea
            data-testid="helpdesk-description"
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Describe the issue"
            value={form.description || ""}
          />
          <label className="flex gap-2 text-sm">
            <input
              data-testid="helpdesk-emergency"
              onChange={(e) =>
                setForm({ ...form, emergency: e.target.checked })
              }
              type="checkbox"
            />
            Emergency issue
          </label>
          <label className="flex gap-2 text-sm">
            <input
              data-testid="helpdesk-restricted"
              onChange={(e) =>
                setForm({
                  ...form,
                  visibility: e.target.checked ? "RESTRICTED" : "NORMAL",
                })
              }
              type="checkbox"
            />
            Restricted visibility
          </label>
          <Button data-testid="helpdesk-submit" onClick={save}>
            Create ticket
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryPanel({ categories, onChanged }) {
  const [name, setName] = useState("");
  const add = async () => {
    try {
      await api.post("/society-helpdesk/categories", { name });
      setName("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add category");
    }
  };
  return (
    <details
      className="mt-5 border bg-card p-4"
      data-testid="helpdesk-category-panel"
    >
      <summary className="cursor-pointer font-display font-bold">
        Category configuration
      </summary>
      <div className="mt-4 flex flex-wrap gap-2">
        <Input
          data-testid="helpdesk-category-name"
          onChange={(e) => setName(e.target.value)}
          placeholder="New category"
          value={name}
        />
        <Button
          data-testid="helpdesk-add-category"
          onClick={add}
          variant="outline"
        >
          Add category
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        {categories.map((x) => (
          <span className="border px-2 py-1" key={x.category_id}>
            {x.name} · {x.default_priority}
          </span>
        ))}
      </div>
    </details>
  );
}

function TicketDetail({ boot, id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const load = async () => {
    if (!id) return;
    try {
      const r = await api.get(`/society-helpdesk/tickets/${id}`);
      setData(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ticket unavailable");
      onClose();
    }
  };
  useEffect(() => {
    setData(null);
    load();
  }, [id]);
  const status = async (value) => {
    const reason = ["REOPENED", "CANCELLED"].includes(value)
      ? window.prompt("Reason")
      : "";
    const resolution_summary =
      value === "RESOLVED" ? window.prompt("Resolution summary") : "";
    if (
      (["REOPENED", "CANCELLED"].includes(value) && !reason) ||
      (value === "RESOLVED" && !resolution_summary)
    )
      return;
    try {
      await api.post(`/society-helpdesk/tickets/${id}/status`, {
        status: value,
        reason,
        resolution_summary,
      });
      toast.success("Status updated");
      load();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    }
  };
  const assign = async (value, staff = false) => {
    try {
      await api.post(`/society-helpdesk/tickets/${id}/assign`, {
        assignee_user_id: staff ? "" : value,
        assignee_staff_id: staff ? value : "",
      });
      toast.success("Assignment saved");
      load();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    }
  };
  const addComment = async () => {
    try {
      await api.post(`/society-helpdesk/tickets/${id}/comments`, {
        text: comment,
        visibility: internal ? "INTERNAL" : "PUBLIC",
      });
      setComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add update");
    }
  };
  if (!id) return null;
  return (
    <Dialog open={!!id} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        data-testid="helpdesk-detail-dialog"
      >
        {!data ? (
          <Loading />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                {data.ticket.ticket_number} · {data.ticket.subject}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-wrap gap-2 text-sm">
              <span>{data.ticket.status}</span>
              <span>· {data.ticket.priority}</span>
              <span>· SLA {data.ticket.sla.status}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {data.ticket.description || "No description."}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <select
                data-testid="helpdesk-assign-select"
                onChange={(e) => assign(e.target.value)}
                value={data.ticket.assignee_user_id || ""}
              >
                <option value="">Assign user</option>
                {boot.assignees.map((x) => (
                  <option key={x.user_id} value={x.user_id}>
                    {x.name || x.email}
                  </option>
                ))}
              </select>
              <select
                data-testid="helpdesk-assign-staff-select"
                onChange={(e) => assign(e.target.value, true)}
                value={data.ticket.assignee_staff_id || ""}
              >
                <option value="">Assign Society staff</option>
                {(boot.staff_assignees || []).map((staff) => (
                  <option key={staff.staff_id} value={staff.staff_id}>
                    {staff.name}
                    {staff.designation_name
                      ? ` · ${staff.designation_name}`
                      : ""}
                  </option>
                ))}
              </select>
              <select
                data-testid="helpdesk-status-select"
                onChange={(e) => status(e.target.value)}
                value=""
              >
                <option value="">Change status</option>
                {[
                  "ACKNOWLEDGED",
                  "IN_PROGRESS",
                  "ON_HOLD",
                  "WAITING_FOR_RESIDENT",
                  "WAITING_FOR_VENDOR",
                  "RESOLVED",
                  "CLOSED",
                  "REOPENED",
                  "CANCELLED",
                ].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-display font-bold">Timeline & updates</h3>
              <div className="mt-3 space-y-2">
                {data.ticket.timeline.map((x) => (
                  <div
                    className="border-l-2 border-primary/30 pl-3 text-sm"
                    key={x.event_id}
                  >
                    <b>{x.kind}</b> · {x.actor_name}{" "}
                    <span className="text-muted-foreground">{x.at}</span>
                    {x.detail && <div>{x.detail}</div>}
                  </div>
                ))}
              </div>
              <div className="mt-4 grid gap-2">
                <Textarea
                  data-testid="helpdesk-comment"
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add requester-visible update"
                  value={comment}
                />
                <label className="text-sm">
                  <input
                    data-testid="helpdesk-internal-note"
                    checked={internal}
                    onChange={(e) => setInternal(e.target.checked)}
                    type="checkbox"
                  />{" "}
                  Internal note
                </label>
                <Button
                  data-testid="helpdesk-add-comment"
                  onClick={addComment}
                  variant="outline"
                >
                  Add update
                </Button>
              </div>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-display font-bold">Attachments</h3>
              <input
                data-testid="helpdesk-attachment-input"
                onChange={async (e) => {
                  if (!e.target.files?.[0]) return;
                  const f = new FormData();
                  f.append("file", e.target.files[0]);
                  f.append("stage", "EVIDENCE");
                  try {
                    await api.post(
                      `/society-helpdesk/tickets/${id}/attachments`,
                      f,
                    );
                    toast.success("Attachment added");
                    load();
                  } catch (x) {
                    toast.error(x.response?.data?.detail || "Upload failed");
                  }
                }}
                type="file"
              />
              {data.attachments.map((x) => (
                <a
                  className="block text-sm text-primary"
                  data-testid={`helpdesk-attachment-${x.attachment_id}`}
                  href={`${API}/society-helpdesk/attachments/${x.attachment_id}/download`}
                  key={x.attachment_id}
                >
                  {x.filename} · {x.stage}
                </a>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
